import { templateChangePartnerMenu } from '../view/template';
import { BaseWebComponentController } from '../../../../framework/BaseWebComponentController';
import { ChangePartnerMenuView } from '../view/ChangePartnerMenuView';
import { ChangePartnerMenuWebComponent } from '../component/ChangePartnerMenuWebComponent';
import { CHANGE_PARTNER_EVENT, CLOSE_PARTNER_MENU_EVENT } from '../types/types';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { promisify } from 'util';
import { ERROR_CODE_MDR_AFFECTED_COMPANIES } from '../../../../types/common/mdrTypes';
import { fetchAffectedCompaniesImportingInaccessibleMdrContacts } from '../../../../utils/mdr.utils';
import { ConsoleException } from '@nirvana/shared/clients/console/nestjs';

export class ChangePartnerMenuController extends BaseWebComponentController {
  public component: ChangePartnerMenuWebComponent;
  public view: ChangePartnerMenuView;
  public viewConstructor: typeof ChangePartnerMenuView;

  constructor(component: ChangePartnerMenuWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: ChangePartnerMenuView,
      viewTemplate: templateChangePartnerMenu,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$changePartnerButton,
        event: 'click',
        callback: this.changePartner,
      },
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.closeChangePartnerSideMenu,
      },
    ];
  }

  public async reloadMenu() {
    await this.render();

    // reset the value
    this.view.dom.$companyIdFormField.value = '';
    this.view.dom.$companyIdFormField.resetField();
    this.view.dom.$companyIdFormField.validate();

    this.hideChangePartnerError();
  }

  private closeChangePartnerSideMenu() {
    this.component.dispatchEvent(
      new CustomEvent(CLOSE_PARTNER_MENU_EVENT, {
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private async changePartner() {
    const newParentId = this.view.dom.$companyIdFormField.value.toLowerCase();

    this.view.dom.$changePartnerButton.showSpinner = true;
    this.view.dom.$changePartnerButton.disabled = true;
    this.view.dom.$cancelButton.disabled = true;

    try {
      // hide backend error when submitting
      this.hideChangePartnerError();
      await this.dependencies.api.clients.Company.ChangePartnerForCurrentCompany(newParentId).toPromise();

      await promisify(this.dependencies.IntegrationsLib.self.reloadStores)();

      this.component.dispatchEvent(
        new CustomEvent(CHANGE_PARTNER_EVENT, {
          bubbles: true,
          cancelable: true,
          composed: false,
        })
      );

      this.showSuccessToast();
      this.closeChangePartnerSideMenu();
    } catch (error) {
      await this.showChangePartnerError(error, newParentId);
    } finally {
      this.view.dom.$changePartnerButton.showSpinner = false;
      this.view.dom.$changePartnerButton.disabled = false;
      this.view.dom.$cancelButton.disabled = false;
    }
  }

  private async showChangePartnerError(error: ConsoleException, newParentHash: string): Promise<void> {
    if (error?.code === ERROR_CODE_MDR_AFFECTED_COMPANIES) {
      await this.setBackendErrorTextInnerHTMLWithMdrAffectedCompanies(newParentHash);
    } else {
      this.view.dom.$backendErrorText.innerText = error?.message || this.translator.getGenericErrorMessage();
    }

    this.view.dom.$backendErrorContainer.classList.toggle('hidden', false);

    this.view.dom.$innerContainer.scrollTo({
      behavior: 'smooth',
      top: this.view.dom.$innerContainer.scrollHeight,
    });
  }

  private async setBackendErrorTextInnerHTMLWithMdrAffectedCompanies(newParentHash: string): Promise<void> {
    const isCustomer = !this.component.company.specifics.rights.manageCompanies;

    if (isCustomer) {
      this.view.setMdrCustomerBackendErrorText();
      return;
    }

    const myCompanyId = this.component.company._id;
    const response = await fetchAffectedCompaniesImportingInaccessibleMdrContacts([myCompanyId], newParentHash);

    this.view.setMdrPartnerBackendErrorText(response[myCompanyId]);
  }

  private hideChangePartnerError() {
    this.view.dom.$backendErrorContainer.classList.toggle('hidden', true);
  }

  private showSuccessToast() {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: this.dependencies.translateFn('Partner change completed successfully.'),
      severity: ToastSeverity.SUCCESS,
    });
  }
}
